import React, { useContext, useRef, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, Modal, NotificationManagerContext,
   Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import CloseIcon from 'assets/icons/close'
import { Input, Textarea } from 'src/components/ConnectedForm'
import HtmlEditor from 'src/components/utils/HtmlEditor'
import ImageUpload from 'src/components/utils/ImageUpload'
import { useFileUploads, useStatefulRef } from 'src/components/utils'
import { useOrganization, useUpdateInitiative } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import style from 'src/style'

const BlogSettings = ({ modalOnCloseComplete, modalOnCloseTrigger, modalOpen }) => {
   const editorRef = useRef(null)
   const [editorScrollContainer, setEditorScrollContainer] = useStatefulRef(null)

   const { organization } = useOrganization()
   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updateInitiative } = useUpdateInitiative()
   const { uploadFile, uploadedFiles, uploadsInProgress, resetFileUploadState } =
      useFileUploads({ targetId: initiative.id, targetType: 'initiative' })

   const { highlightColor: buttonColor } = organization.theme
   const { showNotification } = useContext(NotificationManagerContext)
   const stylesheet = style.stylesheet({ ...organization.typeStyles })

   const [isSaving, setIsSaving] = useState(false)
   const [isPublishing, setIsPublishing] = useState(false)
   const isInactive = ['admin', 'draft'].includes(initiative.privacy)
   let publishButtonMode = isPublishing ? 'loading' : 'normal'
   if (isSaving) {
      publishButtonMode = 'disabled'
   }

   let saveButtonMode = (isSaving || uploadsInProgress.length > 0)
      ? 'loading'
      : 'normal'

   if (isPublishing) {
      saveButtonMode = 'disabled'
   }

   const editorEventConfigProps = {
      events: {
         'image.beforeUpload': async (images) => {
            const currentEditor = editorRef.current.editor
            const { uploadedMediaUrl: imgUrl } = await uploadFile({
               file: images[0],
               purpose: 'contentEmbed'
            })

            currentEditor.image.insert(imgUrl, null, null, currentEditor.image.get())
         }
      }
   }

   const handleSaveInitiative = async (values) => {
      if (uploadsInProgress.length) {
         showNotification({
            message: 'Please wait until your files are done uploading',
            title: 'Error',
            type: 'error'
         })

         return
      }

      try {
         await updateInitiative({ id: initiative.id }, {
            ...values,
            body: [{
               operation: 'updateRichText',
               updateRichText: {
                  id: initiative.body.elements[0].id,
                  body: editorRef.current.editor.html.get(true)
               }
            }],
            coverMedia: uploadedFiles?.coverMedia,
            tags: values.tags.trim().length > 0
               ? values.tags.split(',')
               : []
         })

         resetFileUploadState()
         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err.message,
            title: 'Error!',
            type: 'error'
         })
      } finally {
         setIsSaving(false)
         setIsPublishing(false)
      }
   }

   return (
      <Modal
         borderRadius="3px"
         overlayBackgroundColor="rgba(45,40,40,0.9)"
         width="100%[a-b] 90%[c-d] 100%[e-f]"
         height="100%"
         maxWidth="1000px"
         onCloseComplete={modalOnCloseComplete}
         open={modalOpen}>
         <Formik
            initialValues={{
               name: initiative.name,
               subtitle: initiative.subtitle,
               tags: initiative.tags.join(', ')
            }}
            onSubmit={handleSaveInitiative}
            render={({ handleSubmit, setFieldValue }) => (
               <Form
                  elementAppearance="plain"
                  elementBackgroundColor="#f8f8f8"
                  elementFocusBackgroundColor="#f3f3f3"
                  labelTextColor="#000"
                  labelTextSize="2.5"
                  labelTextTransform="none"
                  labelTextWeight="medium"
                  onSubmit={handleSubmit}>
                  <View
                     position="absolute"
                     top="0px"
                     left="0px"
                     right="0px"
                     bottom="0px"
                     borderRadius="5px"
                     style={{ overflow: 'hidden' }}>
                     <Helmet
                        title={`${initiative.type.nameSingular} Edit | ${initiative.name}`}
                     />
                     <View
                        display="flex"
                        justifyContent="flex-end[a] space-between[b-f]"
                        alignItems="center"
                        position="absolute"
                        top="0px"
                        right="0px"
                        left="0px"
                        height="70px"
                        backgroundColor="#fafafa"
                        borderBottom="1px solid #e5e5e5"
                        padding="0px 0px 0px 30px"
                        zIndex="2">
                        <View display="none[a] flex[b-f]">
                           <Text size="3" weight="medium" color="#333">
                              {`Edit ${initiative.type.nameSingular}`}
                           </Text>
                        </View>
                        <View display="flex" alignItems="center">
                           <Button
                              onClick={() => {
                                 setIsSaving(true)
                                 // This is in the event the user attempted to post. failed,
                                 // then saved draft
                                 if (isInactive) {
                                    setFieldValue('privacy', 'draft', false)
                                 }
                                 handleSubmit()
                              }}
                              color={isSaving ? '#111' : '#e5e5e5'}
                              mode={saveButtonMode}
                              name={isInactive ? 'Save Draft' : 'Save Changes'}
                              padding="25px"
                              size="sm"
                              textColor="#000"
                              type="submit"
                           />
                           {isInactive && (
                              <>
                                 <Spacer size="1" orientation="vertical" />
                                 <Button
                                    onClick={() => {
                                       setIsPublishing(true)
                                       setFieldValue('privacy', 'inherit', false)
                                       handleSubmit()
                                    }}
                                    color={buttonColor}
                                    mode={publishButtonMode}
                                    padding="25px"
                                    size="sm"
                                    textColor="#fff"
                                    type="submit"
                                    name="Post"
                                 />
                              </>
                           )}
                           <View
                              onClick={modalOnCloseTrigger}
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              height="70px"
                              width="70px"
                              marginLeft="20px"
                              borderLeft="1px solid #e5e5e5"
                              style={{ cursor: 'pointer' }}>
                              <CloseIcon width="24px" height="24px" color="#666" />
                           </View>
                        </View>
                     </View>
                     <View
                        position="absolute"
                        top="0px"
                        left="0px"
                        bottom="0px"
                        right="0px"
                        backgroundColor="#fafafa">
                        <View
                           ref={setEditorScrollContainer}
                           position="absolute"
                           top="70px"
                           left="0px"
                           bottom="0px"
                           right="0px"
                           scroll="on">
                           <View width="100%">
                              <View
                                 float="left"
                                 width="100%[a-b] calc(100% - 300px)[c-f]"
                                 minHeight="100vh[c-f]"
                                 borderRight="1px solid #e5e5e5[c-f]"
                                 backgroundColor="#fff">
                                 {isInactive && (
                                    <View
                                       width="100%"
                                       padding="20px 30px"
                                       backgroundColor="rgba(255, 235, 131, 0.2)">
                                       <Text size="1.5[a-b] 2[c-f]" color="rgba(163, 117, 75, 1)">
                                          You are currently in Draft mode
                                       </Text>
                                    </View>
                                 )}
                                 <View
                                    width="100%"
                                    borderRadius="5px"
                                    padding="30px">
                                    <Input
                                       size="xl"
                                       maxLength="120"
                                       name="name"
                                       placeholder={`Enter a title for your ${initiative.type.nameSingular}...`}
                                       style={{
                                          padding: 0,
                                          fontSize: 32,
                                          fontWeight: '500'
                                       }}
                                    />
                                    <Spacer size="3" />
                                    <Text size="2" weight="medium" color="#333">
                                       Subtitle
                                    </Text>
                                    <Spacer size="1" />
                                    <Input
                                       size="md"
                                       name="subtitle"
                                       placeholder="Enter a subtitle"
                                    />
                                    <Spacer size="3" />
                                    <Text size="2" weight="medium" color="#333">
                                       Body
                                    </Text>
                                    <Spacer size="1" />
                                    <View display="block" width="100%">
                                       <HtmlEditor
                                          ref={editorRef}
                                          scrollContainer={editorScrollContainer}
                                          scrollContainerTopOffset="70px"
                                          config={{
                                             heightMin: 400,
                                             paragraphFormat: {
                                                N: 'Body',
                                                pre: 'Code'
                                             },
                                             placeholderText: `Write more about this ${initiative.type.nameSingular}...`,
                                             ...editorEventConfigProps
                                          }}
                                          initialValue={initiative.body?.elements?.[0]?.body}
                                          stylesheet={stylesheet}
                                          toolbarConfig="advanced"
                                       />
                                    </View>
                                    <Spacer size="3" />
                                    <Text size="2" weight="medium" color="#333">
                                       Tags (seperated by commas)
                                    </Text>
                                    <Spacer size="1" />
                                    <Textarea
                                       size="sm"
                                       name="tags"
                                       placeholder="Enter tags..."
                                    />
                                 </View>
                                 <Spacer size="5" />
                              </View>
                              <View float="left" width="300px" padding="20px">
                                 <View
                                    float="left"
                                    width="100%"
                                    backgroundColor="#eee"
                                    padding="10px"
                                    borderRadius="3px"
                                    border="1px solid #ddd">
                                    <ImageUpload
                                       onFileChange={uploadFile}
                                       aspectRatio={0.6}
                                       name="coverMedia"
                                       emptyMessage="No cover image set"
                                       purpose="coverPhoto"
                                       src={initiative.coverMedia?.file.thumbnailUrlW480}
                                    />
                                 </View>
                              </View>
                           </View>
                        </View>
                     </View>
                  </View>
               </Form>
            )}
         />
      </Modal>
   )
}

BlogSettings.propTypes = {
   modalOnCloseComplete: PropTypes.func.isRequired,
   modalOnCloseTrigger: PropTypes.func.isRequired,
   modalOpen: PropTypes.bool.isRequired
}

export default BlogSettings
